import PasswordInput from '../../components/PasswordInput';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  GlobeAltIcon,
  UserPlusIcon,
  ArrowLeftIcon,
  XMarkIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

  .sd-root * { box-sizing: border-box; }

  .sd-root {
    font-family: 'Sora', sans-serif;
    background: #f0f2f7;
    min-height: 100vh;
    color: #1a1d2e;
    padding: 2rem;
  }

  /* HEADER */
  .sd-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.78rem;
    font-weight: 500;
    color: #8b90a7;
    text-decoration: none;
    margin-bottom: 1.5rem;
    transition: color 0.15s;
  }
  .sd-back:hover { color: #3b6ef8; }
  .sd-back svg { width: 14px; height: 14px; }

  .sd-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 1.75rem;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .sd-title {
    font-size: 1.75rem;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: #0f1123;
  }

  .sd-subdomain {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.78rem;
    color: #8b90a7;
    margin-top: 4px;
  }

  /* STATUS BADGE */
  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 600;
    padding: 5px 12px;
    border-radius: 99px;
    background: #ecfdf5;
    color: #10c98e;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .status-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #10c98e;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.4; }
  }

  /* LAYOUT */
  .sd-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 760px) { .sd-grid { grid-template-columns: 1fr; } }

  /* PANELS */
  .panel {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    border: 1px solid #e8ebf4;
    animation: fadeUp 0.4s ease both;
  }

  .panel:nth-child(1) { animation-delay: 0.05s; }
  .panel:nth-child(2) { animation-delay: 0.10s; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .panel-title {
    font-size: 0.92rem;
    font-weight: 600;
    color: #1a1d2e;
    margin-bottom: 1.25rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #f0f2f7;
  }

  /* INFO ROWS */
  .info-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid #f3f5fb;
  }

  .info-row:last-child { border-bottom: none; }

  .info-icon-wrap {
    width: 34px; height: 34px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .info-icon-wrap.blue   { background: #eef2ff; color: #3b6ef8; }
  .info-icon-wrap.green  { background: #ecfdf5; color: #10c98e; }
  .info-icon-wrap.purple { background: #f3f0ff; color: #8b5cf6; }
  .info-icon-wrap.amber  { background: #fffbeb; color: #f59e0b; }

  .info-icon-wrap svg { width: 16px; height: 16px; stroke-width: 1.8; }

  .info-label {
    font-size: 0.72rem;
    font-weight: 500;
    color: #9094a8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 2px;
  }

  .info-value {
    font-size: 0.86rem;
    color: #1a1d2e;
    font-weight: 500;
  }

  .info-value.mono {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.82rem;
  }

  /* QUICK STATS inside panel */
  .qs-row {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.25rem;
  }

  .qs-box {
    flex: 1;
    background: #f7f8fc;
    border-radius: 12px;
    padding: 1rem;
    text-align: center;
    border: 1px solid #edf0f9;
  }

  .qs-val {
    font-size: 1.6rem;
    font-weight: 700;
    letter-spacing: -0.04em;
    color: #0f1123;
    line-height: 1;
  }

  .qs-label {
    font-size: 0.7rem;
    font-weight: 500;
    color: #9094a8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: 4px;
  }

  /* ACTION BUTTON */
  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #3b6ef8;
    color: white;
    border: none;
    border-radius: 10px;
    font-family: 'Sora', sans-serif;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  }

  .btn-primary:hover {
    background: #2d5de0;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(59, 110, 248, 0.35);
  }

  .btn-primary svg { width: 16px; height: 16px; stroke-width: 2; }

  /* MODAL OVERLAY */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 17, 35, 0.55);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
    animation: overlayIn 0.2s ease;
  }

  @keyframes overlayIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  /* MODAL CARD */
  .modal-card {
    background: white;
    border-radius: 20px;
    padding: 2rem;
    width: 100%;
    max-width: 440px;
    margin: 1rem;
    border: 1px solid #e8ebf4;
    animation: modalIn 0.25s ease;
    position: relative;
  }

  @keyframes modalIn {
    from { opacity: 0; transform: translateY(16px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }

  .modal-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #0f1123;
    letter-spacing: -0.02em;
  }

  .modal-sub {
    font-size: 0.76rem;
    color: #9094a8;
    margin-top: 2px;
  }

  .modal-close {
    width: 32px; height: 32px;
    border-radius: 8px;
    border: 1px solid #e8ebf4;
    background: white;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: #9094a8;
    transition: background 0.15s, color 0.15s;
  }

  .modal-close:hover { background: #f3f5fb; color: #1a1d2e; }
  .modal-close svg { width: 16px; height: 16px; }

  /* FORM FIELDS */
  .field {
    margin-bottom: 1rem;
  }

  .field label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b7194;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 6px;
  }

  .field input {
    width: 100%;
    padding: 10px 14px;
    border: 1.5px solid #e4e7f0;
    border-radius: 10px;
    font-family: 'Sora', sans-serif;
    font-size: 0.84rem;
    color: #1a1d2e;
    background: #fafbff;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .field input:focus {
    border-color: #3b6ef8;
    box-shadow: 0 0 0 3px rgba(59, 110, 248, 0.1);
    background: white;
  }

  .field input::placeholder { color: #c0c5dc; }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 1.5rem;
  }

  .btn-ghost {
    padding: 10px 18px;
    background: #f3f5fb;
    border: none;
    border-radius: 10px;
    font-family: 'Sora', sans-serif;
    font-size: 0.82rem;
    font-weight: 500;
    color: #6b7194;
    cursor: pointer;
    transition: background 0.15s;
  }

  .btn-ghost:hover { background: #e8ebf4; }

  /* LOADING */
  .loading-wrap {
    display: flex; align-items: center; justify-content: center;
    height: 60vh; font-family: 'Sora', sans-serif;
    color: #9094a8; font-size: 0.9rem; gap: 10px;
  }

  .spinner {
    width: 18px; height: 18px;
    border: 2px solid #e4e7f0;
    border-top-color: #3b6ef8;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }
`;

const SuperSchoolDetail = () => {
  const { schoolId } = useParams();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminData, setAdminData] = useState({ name: '', email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchSchool(); }, [schoolId]);

  const fetchSchool = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/super/schools/${schoolId}`);
      setSchool(data);
    } catch {
      toast.error('Failed to fetch school');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/super/schools/${schoolId}/admin`, adminData);
      toast.success('School admin created');
      setShowAdminModal(false);
      setAdminData({ name: '', email: '', password: '' });
    } catch {
      toast.error('Failed to create admin');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="sd-root">
      <style>{style}</style>
      <div className="loading-wrap"><div className="spinner" /> Loading school…</div>
    </div>
  );

  if (!school) return (
    <div className="sd-root">
      <style>{style}</style>
      <div className="loading-wrap">School not found.</div>
    </div>
  );

  const infoRows = [
    { icon: EnvelopeIcon, color: 'blue',   label: 'Email',     value: school.email,    mono: true  },
    { icon: PhoneIcon,    color: 'green',  label: 'Phone',     value: school.phone,    mono: true  },
    { icon: MapPinIcon,   color: 'purple', label: 'Address',   value: school.address,  mono: false },
    { icon: GlobeAltIcon, color: 'amber',  label: 'Subdomain', value: school.subdomain, mono: true },
  ];

  return (
    <div className="sd-root">
      <style>{style}</style>

      {/* BACK LINK */}
      <Link to="/super/schools" className="sd-back">
        <ArrowLeftIcon /> Back to Schools
      </Link>

      {/* PAGE HEADER */}
      <div className="sd-header">
        <div>
          <h1 className="sd-title">{school.name}</h1>
          <div className="sd-subdomain">{school.subdomain}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span className="status-badge">
            <span className="status-dot" /> Active
          </span>
          <button className="btn-primary" onClick={() => setShowAdminModal(true)}>
            <UserPlusIcon /> Create Admin
          </button>
        </div>
      </div>

      {/* QUICK STATS */}
      <div className="qs-row" style={{ marginBottom: '1rem' }}>
        {[
          { val: school.totalStudents ?? '—', label: 'Students' },
          { val: school.totalTeachers ?? '—', label: 'Teachers' },
          { val: school.totalClasses  ?? '—', label: 'Classes'  },
          { val: school.totalAdmins   ?? '—', label: 'Admins'   },
        ].map((s) => (
          <div key={s.label} className="qs-box">
            <div className="qs-val">{s.val}</div>
            <div className="qs-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* MAIN GRID */}
      <div className="sd-grid">
        {/* School Info */}
        <div className="panel">
          <div className="panel-title">
            <BuildingOfficeIcon style={{ width: 16, height: 16, display: 'inline', marginRight: 6, verticalAlign: 'middle', color: '#3b6ef8' }} />
            School Information
          </div>
          {infoRows.map((row) => (
            <div key={row.label} className="info-row">
              <div className={`info-icon-wrap ${row.color}`}>
                <row.icon />
              </div>
              <div>
                <div className="info-label">{row.label}</div>
                <div className={`info-value${row.mono ? ' mono' : ''}`}>
                  {row.value || <span style={{ color: '#c0c5dc' }}>Not provided</span>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Admin Panel */}
        <div className="panel">
          <div className="panel-title">
            <ShieldCheckIcon style={{ width: 16, height: 16, display: 'inline', marginRight: 6, verticalAlign: 'middle', color: '#8b5cf6' }} />
            Admin Access
          </div>
          {school.admins && school.admins.length > 0 ? (
            school.admins.map((admin, i) => (
              <div key={i} className="info-row">
                <div className="info-icon-wrap purple">
                  <UserPlusIcon />
                </div>
                <div>
                  <div className="info-label">Admin</div>
                  <div className="info-value">{admin.name}</div>
                  <div className="info-value mono" style={{ color: '#9094a8', fontSize: '0.76rem' }}>{admin.email}</div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: '#f3f0ff', color: '#8b5cf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 12px',
              }}>
                <UserPlusIcon style={{ width: 22, height: 22 }} />
              </div>
              <p style={{ fontSize: '0.84rem', fontWeight: 600, color: '#1a1d2e', marginBottom: 4 }}>No admins yet</p>
              <p style={{ fontSize: '0.76rem', color: '#9094a8', marginBottom: 16 }}>
                Create an admin to grant access to this school.
              </p>
              <button className="btn-primary" onClick={() => setShowAdminModal(true)}>
                <UserPlusIcon /> Create Admin
              </button>
            </div>
          )}
        </div>
      </div>

      {/* CREATE ADMIN MODAL */}
      {showAdminModal && createPortal(
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowAdminModal(false)}>
          <div className="modal-card">
            <div className="modal-header">
              <div>
                <div className="modal-title">Create School Admin</div>
                <div className="modal-sub">for {school.name}</div>
              </div>
              <button className="modal-close" onClick={() => setShowAdminModal(false)}>
                <XMarkIcon />
              </button>
            </div>

            <form onSubmit={handleCreateAdmin}>
              <div className="field">
                <label>Full Name</label>
                <input
                  type="text"
                  placeholder="e.g. Ahmed Khan"
                  value={adminData.name}
                  onChange={(e) => setAdminData({ ...adminData, name: e.target.value })}
                  required
                />
              </div>
              <div className="field">
                <label>Email Address</label>
                <input
                  type="email"
                  placeholder="admin@school.com"
                  value={adminData.email}
                  onChange={(e) => setAdminData({ ...adminData, email: e.target.value })}
                  required
                />
              </div>
              <div className="field">
                <label>Password</label>
                <PasswordInput
                  placeholder="Min. 8 characters"
                  value={adminData.password}
                  onChange={(e) => setAdminData({ ...adminData, password: e.target.value })}
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setShowAdminModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Creating…' : <><UserPlusIcon /> Create Admin</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

export default SuperSchoolDetail;